import { AppBar, Avatar, Container, IconButton, Tab, Tabs, Toolbar, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import { AppDispatch } from '../../store'
import { logOut } from '../../store/auth/authSlice'
import TAGLogo from '../LogoTAG'
import NavbarMenu from '../NavbarMenu'
import { INavbarLink } from './types'
import colors from '../../constants/colors'
import changesSelector from '../../store/changes/changesSelector'
import { IAuthJWT } from '../../config/axios'
import jwtDecode from 'jwt-decode'
import authService from '../../services/authService'
import { userRoles } from '../../store/auth/userTypes'
import authSelectors from '../../store/auth/authSelectors'
import { getUserDetails } from '../../store/auth/authSlice'
import { APP_MODE } from '../../sharedTypes'


const navbarLinks: INavbarLink[] = [
  {
    title: 'Cases',
    href: '/cases',
    roles: [userRoles.DESIGNATED_RECEIVER, userRoles.INVESTIGATOR, userRoles.ADMIN],
  },
  {
    title: 'Archive',
    href: '/archive',
    roles: [userRoles.DESIGNATED_RECEIVER, userRoles.INVESTIGATOR, userRoles.ADMIN],
  },
  {
    title: 'Users',
    href: '/users',
    roles: [userRoles.ADMIN],
  },
  {
    title: 'Metrics',
    href: '/metrics',
    roles: [userRoles.ADMIN],
  },
  {
    title: 'Export',
    href: '/export',
    roles: [userRoles.ADMIN],
  },
]



// const getCurrentRoute = () => {
//   const path = window.location.pathname.split('/')
//   path.shift()

//   const itemIndex = navbarLinks.findIndex(item => item?.href === `/${path[0]}`)
//   return itemIndex
// }

const getValidTabValue = () => {
  const path = window.location.pathname.split('/')
  path.shift()

  // Find the index of the current route in navbarLinks
  const itemIndex = navbarLinks.findIndex(item => item?.href === `/${path[0]}`)

  // If not found, return false so MUI doesn't throw "value is invalid"
  return itemIndex >= 0 ? itemIndex : false
}

const Navbar: React.FC = () => {
  // const [value, setValue] = useState(getCurrentRoute() ?? 0)
  const [value, setValue] = useState<number | false>(getValidTabValue())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [initials, setInitials] = useState<string>('')
  const isAuth = useAuth()
  const dispatch = useDispatch<AppDispatch>()
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const location = useLocation()
  const numberOfChanges = useSelector(changesSelector.numberOfChanges)
  const userRoles = useSelector(authSelectors.userRoles)
  const userDetails = useSelector(authSelectors.userDetails)
  const organizationType = useSelector(authSelectors.userOrganizationType)

  const renderLinks = () => {
    return navbarLinks.map(({ title, href, roles }, index) => {
      if (title === 'Metrics' && organizationType === APP_MODE.light) {
        return []
      }
      const found = userRoles?.some(role => roles.includes(role as userRoles))
      if (found) {
        return (
          <Tab
            component={Link}
            label={title}
            tabIndex={index}
            to={href}
            sx={{
              color: `${colors.TAG_TEXT_GRAY} !important`,
              typography: 'h4',
              textTransform: 'capitalize',
              '&.Mui-selected': {
                color: `${colors.TAG_BLUE} !important`,
                borderBottom: `2px solid ${colors.TAG_BLUE}`,
              },
            }}
            key={`${index}${href}`}
          />
        )
      }
      return []
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logOut())
    navigate('/login')
  }
  
  const handleSync = () => {
    const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
    dispatch(getUserDetails({ userId: tokenData.user_id }))
    dispatch
  }

  const handleChange = (event: React.SyntheticEvent, newValue: any) => {
    //event.preventDefault()
    if (numberOfChanges) {
      return
    }
    setValue(newValue)
  }
  const getUserData = async () => {
    if (!userDetails || !userDetails.user) {
      const tokenData: IAuthJWT = jwtDecode(localStorage.getItem('accessToken') || '')
      const { data: userDetails } = await authService.getUserDetails(tokenData?.user_id)
      const firstNameInitial = userDetails?.user?.first_name[0]
      const lastNameInitial = userDetails?.user?.last_name[0]
      setInitials(`${firstNameInitial ?? ''}${lastNameInitial ?? ''}`)
    }else{
      const user = userDetails?.user;
      const firstNameInitial = user.first_name ? user.first_name[0] : 'A';
      const lastNameInitial = user.last_name ? user.last_name[0] : 'A';

      setInitials(`${firstNameInitial ?? ''}${lastNameInitial ?? ''}`)

    }
  }

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      const tokenData: IAuthJWT = jwtDecode(token)
      dispatch(getUserDetails({ userId: tokenData.user_id }))
    }
    getUserData()
  }, [dispatch])
  
  useEffect(() => {
    // Update tab selection on route change
    setValue(getValidTabValue())
  }, [location.pathname])

  return (
    <AppBar sx={{ padding: '2rem' }} className={'t-viewport-header'}>
      <Toolbar
        sx={{
          width: '100%',
          gap: '28px',
          justifyContent: 'space-between',
          padding: '0 !important',
        }}
      >
        <TAGLogo />
        {isAuth && window.innerWidth > 1023 && (
          <>
            <Container
              disableGutters
              maxWidth={false}
              sx={{ display: 'flex', alignItems: 'center', gap: '28px' }}
            >
              {
                <Tabs value={value} onChange={handleChange} TabIndicatorProps={{ style: { backgroundColor: colors.TAG_BLUE, height: '2px' } }}>
                  {renderLinks()}
                </Tabs>
              }
            </Container>
            <div>
              <Tooltip title='Logout'>
                <span>
                <IconButton
                  onClick={handleClick}
                  size='small'
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'account-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={open ? 'true' : undefined}
                  style={{ padding: 0 }}
                >
                  <Avatar
                    className='dafault-font-family'
                    sx={{
                      width: 50,
                      height: 50,
                      backgroundColor: colors.WHITE,
                      color: colors.BLACK,
                      border: '1px solid black',
                      textTransform: 'uppercase',
                    }}
                  >
                    {initials ?? 'JD'}
                  </Avatar>
                </IconButton>
                </span>
              </Tooltip>
            </div>
          </>
        )}
      </Toolbar>
      <NavbarMenu
        open={open}
        anchorEl={anchorEl}
        handleClose={handleClose}
        handleLogout={handleLogout}
        handleSync={handleSync}
      />
    </AppBar>
  )
}

export default Navbar
